/**
 * RWBR — LIVERIES.
 *
 * The printed cards carry an OVERHEAD drawing of a stock car — nose left, four
 * tyres proud of the bodywork, the number on the roof, a manufacturer tab on
 * the nose. This module is the house layer that gives every car its paint, and
 * it is a DEVIATION FROM THE PRINTED COMPONENT — the boards carry a toggle that
 * turns it off and puts every car back in printed black (`PRINTED_LIVERY`).
 *
 * NOTHING HERE IS A RULE. No table, roll or ruling anywhere in the engine reads
 * a colour. A livery is decoration and identity; every fact a player needs is
 * carried as text somewhere else on the card.
 *
 * FOUR PROMISES
 *
 * 1. DETERMINISM. `liveryFor(seed)` is a pure function of its string. A team
 *    painted royal blue on Tuesday is royal blue after a reload, a re-seed of
 *    the race, or a rebuild of the app. The seed is a team id (or a driver id
 *    for an independent), never an array index — adding a car to the field must
 *    not repaint anyone else's.
 *
 * 2. LEGIBILITY, COMPUTED. The car number is the one piece of information the
 *    paint could destroy, so it is never chosen by taste. `numberPanel` names
 *    the panel the number is actually printed on — the roof, which is `base` on
 *    every scheme except `roof-panel`, where the roof IS the accent — and
 *    `numberColor` is whichever of white/black wins the WCAG contrast test
 *    against THAT. `numberOutline` is the loser, added back as a thin edge
 *    whenever the winner's ratio is under OUTLINE_BELOW. The same arithmetic
 *    picks the ink for the manufacturer tab.
 *
 * 3. A CURATED PALETTE, NOT A HUE WHEEL. Random hues produce mud: olive,
 *    mauve, dishwater grey. Real stock cars are painted out of a short list of
 *    saturated, high-contrast colours, and each of those colours has a short
 *    list of accents it is actually seen with. Both lists are hand-written
 *    below and the tests hold them to a minimum contrast so no pair can vanish
 *    into itself.
 *
 * 4. A FIELD IS NOT A MONOCHROME. Hashing a team id straight into the palette
 *    gives collisions at exactly the rate the birthday problem says it should —
 *    fourteen teams out of thirteen colours once put six purple cars on the
 *    same board. `assignInks` deals the palette out ONCE PER FIELD, in team
 *    order, probing forward from each team's hashed slot until it finds a
 *    colour nobody has taken. Twenty-one bases against fourteen charters means
 *    no two shops in a full field are ever painted alike.
 *
 * TEAM FAMILIES. A team's three cars share `base` — that is what makes them
 * read as one garage from across the room — and differ in accent, scheme and
 * decal, so you can still tell which of the three you are looking at. They also
 * share a MANUFACTURER, because a shop runs one make. `liveryFor`'s second
 * argument is the variant seed: pass a team id first and a driver id (or a
 * member index, via `liveryForTeamMember`) second.
 */

import { makeRng } from './dice.ts';

// ---------------------------------------------------------------------------
// The type
// ---------------------------------------------------------------------------

/**
 * How the accent is laid onto the body, SEEN FROM ABOVE. These are the five
 * things the real cards actually do: nothing, a nose, a tail, a roof, a stripe.
 */
export type LiveryScheme =
  | 'solid'
  | 'two-tone-nose'
  | 'two-tone-tail'
  | 'roof-panel'
  | 'centre-stripe';

export const LIVERY_SCHEMES: readonly LiveryScheme[] = [
  'solid',
  'two-tone-nose',
  'two-tone-tail',
  'roof-panel',
  'centre-stripe',
];

/** The badge on the rear deck. `none` is the commonest answer and stays so. */
export type Decal = 'none' | 'oval' | 'star' | 'roundel';

export const DECALS: readonly Decal[] = ['none', 'oval', 'star', 'roundel'];

/**
 * The wheel the decal is actually drawn off. Half of it is `none` on purpose:
 * forty-three cars all wearing a badge is a sticker sheet, not a field.
 */
export const DECAL_WHEEL: readonly Decal[] = ['none', 'oval', 'none', 'star', 'none', 'roundel'];

/** The four makes on the printed nose tabs. */
export type Manufacturer = 'Ford' | 'Chevy' | 'Pontiac' | 'Toyota';

export const MANUFACTURERS: readonly Manufacturer[] = ['Ford', 'Chevy', 'Pontiac', 'Toyota'];

export interface Livery {
  /** The body colour. Every car on a team shares this. */
  base: string;
  /** The second colour: nose, tail, roof, stripe and decal, per `scheme`. */
  accent: string;
  /**
   * The colour of the panel the NUMBER sits on — the roof. `base` everywhere
   * except `roof-panel`, where the roof is the accent. This is the panel
   * `numberColor` was computed against and the only one it has to survive.
   */
  numberPanel: string;
  /** White or black, whichever wins the contrast test against `numberPanel`. */
  numberColor: string;
  /** The losing colour, as a thin edge, or null when the number needs no help. */
  numberOutline: string | null;
  scheme: LiveryScheme;
  /** The badge on the rear deck, drawn in the accent (or the base, if the deck is accent). */
  decal: Decal;
  /** The make on the nose tab. One per shop. */
  manufacturer: Manufacturer;
  /** The nose tab's block colour — never the colour of the nose panel behind it. */
  tabColor: string;
  /** White or black, whichever wins against `tabColor`. */
  tabInk: string;
}

// ---------------------------------------------------------------------------
// The palette
// ---------------------------------------------------------------------------

/**
 * The ink names are the ones a paint shop would use. Hexes are chosen
 * saturated and away from the middle of the value range: a body colour that
 * lands near 50% luminance is the one that leaves a number illegible in both
 * white and black, so there are none here.
 *
 * TWENTY-ONE OF THEM, and the count is the point. Thirteen bases across
 * fourteen charters guarantees a repeat before `assignInks` has anywhere to go;
 * twenty-one leaves seven spare, which is what makes a full field's shops all
 * different colours instead of nearly all different colours.
 *
 * Listed dark-to-light within each hue family so `Object.values(INKS)` is a
 * meaningful order for the scatter test.
 */
export const INKS = {
  white: '#F2F4F7',
  silver: '#B7BDC6',
  gunmetal: '#3A4048',
  black: '#101014',
  red: '#C8102E',
  wine: '#7A1526',
  copper: '#9C4520',
  orange: '#F4600B',
  gold: '#C9962B',
  chrome: '#FFC20E',
  lime: '#A8CE38',
  kelly: '#00A550',
  forest: '#14532D',
  aqua: '#2FBF9E',
  teal: '#007A8A',
  sky: '#4FB3E8',
  electric: '#0E86D9',
  cobalt: '#1F49C0',
  navy: '#0B3D91',
  purple: '#5B2A9B',
  magenta: '#B01060',
} as const;

export type InkName = keyof typeof INKS;

/**
 * A body colour and the accents it is actually painted with.
 *
 * Every pair here clears MIN_ACCENT_CONTRAST — see `livery.test.ts`, which
 * walks the whole table. Pairs that failed were not softened, they were
 * deleted: purple on black and forest on black both read as one dark blob at
 * card size, and electric blue beside orange reads as neither.
 *
 * EVERY ACCENT LIST IS 3, 4 OR 5 LONG and that is load-bearing — see the stride
 * in `liveryForTeamMember`.
 */
interface Family {
  base: InkName;
  accents: readonly InkName[];
}

export const FAMILIES: readonly Family[] = [
  { base: 'white', accents: ['red', 'navy', 'black', 'orange', 'kelly'] },
  { base: 'silver', accents: ['black', 'red', 'navy', 'purple'] },
  { base: 'gunmetal', accents: ['white', 'chrome', 'orange', 'sky'] },
  { base: 'black', accents: ['chrome', 'red', 'white', 'orange', 'electric'] },
  { base: 'red', accents: ['white', 'black', 'chrome', 'silver'] },
  { base: 'wine', accents: ['white', 'chrome', 'silver', 'gold'] },
  { base: 'copper', accents: ['white', 'chrome', 'silver'] },
  { base: 'orange', accents: ['black', 'white', 'navy', 'forest'] },
  { base: 'gold', accents: ['black', 'navy', 'wine', 'purple'] },
  { base: 'chrome', accents: ['black', 'navy', 'red', 'purple'] },
  { base: 'lime', accents: ['black', 'navy', 'gunmetal', 'magenta'] },
  { base: 'kelly', accents: ['white', 'black', 'navy'] },
  { base: 'forest', accents: ['white', 'chrome', 'silver', 'lime'] },
  { base: 'aqua', accents: ['black', 'navy', 'wine', 'magenta'] },
  { base: 'teal', accents: ['white', 'chrome', 'black'] },
  { base: 'sky', accents: ['black', 'navy', 'wine', 'purple'] },
  { base: 'electric', accents: ['white', 'black', 'chrome'] },
  { base: 'cobalt', accents: ['white', 'chrome', 'orange', 'lime'] },
  { base: 'navy', accents: ['white', 'chrome', 'orange', 'silver'] },
  { base: 'purple', accents: ['white', 'chrome', 'silver', 'lime'] },
  { base: 'magenta', accents: ['white', 'chrome', 'gold', 'lime'] },
];

/** The number is one of these two and nothing else. */
export const NUMBER_LIGHT = '#FFFFFF';
export const NUMBER_DARK = '#0B0B0D';

/** Below this contrast ratio the number gets an outline in the other colour. */
export const OUTLINE_BELOW = 7;

/** No base/accent pair in FAMILIES may fall under this. Held by the tests. */
export const MIN_ACCENT_CONTRAST = 2;

/** The printed card: solid black car, light number, no second colour. */
export const PRINTED_LIVERY: Livery = {
  base: '#16150F',
  accent: '#16150F',
  numberPanel: '#16150F',
  numberColor: '#F5F1E6',
  numberOutline: null,
  scheme: 'solid',
  decal: 'none',
  manufacturer: 'Ford',
  tabColor: '#F5F1E6',
  tabInk: NUMBER_DARK,
};

/**
 * The same car as the card prints it — black ink, no paint — but STILL WEARING
 * ITS OWN MAKE, because the manufacturer tab is printed on the physical card.
 * Turning the house colour layer off must not turn every Chevy into a Ford.
 */
export function asPrinted(livery: Livery): Livery {
  return { ...PRINTED_LIVERY, manufacturer: livery.manufacturer };
}

// ---------------------------------------------------------------------------
// Colour arithmetic
// ---------------------------------------------------------------------------

function channel(hex: string, at: number): number {
  return parseInt(hex.slice(at, at + 2), 16);
}

/** WCAG relative luminance, 0 (black) to 1 (white). Accepts `#rrggbb`. */
export function relativeLuminance(hex: string): number {
  const lin = (v: number): number => {
    const s = v / 255;
    return s <= 0.04045 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  };
  const r = lin(channel(hex, 1));
  const g = lin(channel(hex, 3));
  const b = lin(channel(hex, 5));
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/** WCAG contrast ratio, 1 (identical) to 21 (black on white). Order-free. */
export function contrastRatio(a: string, b: string): number {
  const la = relativeLuminance(a);
  const lb = relativeLuminance(b);
  const hi = Math.max(la, lb);
  const lo = Math.min(la, lb);
  return (hi + 0.05) / (lo + 0.05);
}

/**
 * The number treatment for a panel: the more legible of white/black, plus the
 * other one as an outline when the winner is not clear enough on its own.
 *
 * This is the whole of the legibility guarantee and it is deliberately not a
 * lookup table — add a colour to INKS and it is handled.
 */
export function numberTreatment(panel: string): { color: string; outline: string | null } {
  const light = contrastRatio(NUMBER_LIGHT, panel);
  const dark = contrastRatio(NUMBER_DARK, panel);
  const color = light >= dark ? NUMBER_LIGHT : NUMBER_DARK;
  const won = Math.max(light, dark);
  const outline = won < OUTLINE_BELOW ? (color === NUMBER_LIGHT ? NUMBER_DARK : NUMBER_LIGHT) : null;
  return { color, outline };
}

// ---------------------------------------------------------------------------
// Seeding
// ---------------------------------------------------------------------------

/**
 * FNV-1a with a final avalanche.
 *
 * The avalanche is not decoration: the seeds this takes are `tm-01`, `tm-02`,
 * `drv-001`, `drv-002` — strings that differ in one late character. Without the
 * mix, neighbouring ids land in neighbouring palette slots and the field comes
 * out sorted by colour.
 */
export function liverySeedHash(seed: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < seed.length; i++) {
    h ^= seed.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  h ^= h >>> 16;
  h = Math.imul(h, 0x7feb352d) >>> 0;
  h ^= h >>> 15;
  h = Math.imul(h, 0x846ca68b) >>> 0;
  h ^= h >>> 16;
  return h >>> 0;
}

/**
 * Accent, scheme and decal are read out of ONE combined index so that stepping
 * the index by a stride coprime with each of the three periods — which is what
 * `liveryForTeamMember` does — is guaranteed to move all three. That is why
 * teammates never come out in the same paint.
 */
function combo(
  family: Family,
  index: number,
): { accent: InkName; scheme: LiveryScheme; decal: Decal } {
  const n = family.accents.length;
  const period = n * LIVERY_SCHEMES.length * DECAL_WHEEL.length;
  const i = ((index % period) + period) % period;
  return {
    accent: family.accents[i % n],
    scheme: LIVERY_SCHEMES[Math.floor(i / n) % LIVERY_SCHEMES.length],
    decal: DECAL_WHEEL[i % DECAL_WHEEL.length],
  };
}

/** One make per shop, off the shop's own id — not off the car's variant index. */
function manufacturerFor(identity: string): Manufacturer {
  return MANUFACTURERS[liverySeedHash(`${identity}/make`) % MANUFACTURERS.length];
}

function assemble(family: Family, index: number, identity: string): Livery {
  const { accent, scheme, decal } = combo(family, index);
  const base = INKS[family.base];
  const accentHex = INKS[accent];

  // The number lives on the roof. On `roof-panel` the roof IS the accent, so
  // that is the panel the contrast test has to be run against.
  const numberPanel = scheme === 'roof-panel' ? accentHex : base;
  const treatment = numberTreatment(numberPanel);

  // The tab is a block on the nose, and on `two-tone-nose` the nose is already
  // the accent — an accent tab there would be invisible. Base and accent are
  // guaranteed MIN_ACCENT_CONTRAST apart, so the other one always works.
  const nosePanel = scheme === 'two-tone-nose' ? accentHex : base;
  const tabColor = contrastRatio(accentHex, nosePanel) >= MIN_ACCENT_CONTRAST ? accentHex : base;

  return {
    base,
    accent: accentHex,
    numberPanel,
    numberColor: treatment.color,
    numberOutline: treatment.outline,
    scheme,
    decal,
    manufacturer: manufacturerFor(identity),
    tabColor,
    tabInk: numberTreatment(tabColor).color,
  };
}

function familyFor(seed: string): Family {
  return FAMILIES[liverySeedHash(seed) % FAMILIES.length];
}

function familyByBase(base: InkName): Family {
  const found = FAMILIES.find((f) => f.base === base);
  if (!found) throw new RangeError(`no livery family is painted ${base}`);
  return found;
}

// ---------------------------------------------------------------------------
// Dealing the palette out across a field
// ---------------------------------------------------------------------------

/**
 * One base colour per seed, in seed order, with NO TWO THE SAME while the
 * palette still has an unused colour in it.
 *
 * Each seed starts at the slot its own hash picks — so a shop's colour is still
 * its own, not an arbitrary deal — and only moves forward if that slot is
 * already spoken for. Past the end of the palette (more than twenty-one shops)
 * every seed falls back to its hashed slot and repeats become possible again,
 * which is correct: there is no twenty-second colour to give it.
 *
 * Deterministic in the list it is handed. Appending a team never repaints one
 * already dealt.
 */
export function assignInks(seeds: readonly string[]): InkName[] {
  const taken = new Set<number>();
  const out: InkName[] = [];
  for (const seed of seeds) {
    const start = liverySeedHash(seed) % FAMILIES.length;
    let slot = start;
    if (taken.size < FAMILIES.length) {
      for (let step = 0; step < FAMILIES.length; step++) {
        const probe = (start + step) % FAMILIES.length;
        if (!taken.has(probe)) {
          slot = probe;
          break;
        }
      }
    }
    taken.add(slot);
    out.push(FAMILIES[slot].base);
  }
  return out;
}

// ---------------------------------------------------------------------------
// The entry points
// ---------------------------------------------------------------------------

/**
 * The livery for a seed — a team id for a chartered car, a driver id for an
 * independent.
 *
 * `variantSeed` splits the choice in two: the base comes from `seed`, the
 * accent, scheme and decal from `variantSeed`. Pass a team id and a driver id
 * to get one garage's paint in one driver's variation of it.
 *
 * `baseInk` overrides the hashed base with a dealt one (see `assignInks`) and
 * changes nothing else — the shop keeps its own accent, scheme, decal and make.
 */
export function liveryFor(seed: string, variantSeed?: string, baseInk?: InkName): Livery {
  const family = baseInk ? familyByBase(baseInk) : familyFor(seed);
  const rng = makeRng(liverySeedHash(variantSeed ?? `${seed}/v`));
  return assemble(family, Math.floor(rng() * 0x40000000), seed);
}

/**
 * A team car by roster slot.
 *
 * The stride is 7, and 7 is coprime with every accent-list length in FAMILIES
 * (3, 4, 5) AND with the six-slot decal wheel, so members 0, 1 and 2 of a team
 * are GUARANTEED three different accents and three different decal slots — a
 * three-car team can never paint two cars identically. The scheme moves with
 * them and usually differs too; on the three-accent families it can repeat,
 * which is fine, because the accent has already told them apart.
 */
export function liveryForTeamMember(
  teamId: string,
  memberIndex: number,
  baseInk?: InkName,
): Livery {
  const family = baseInk ? familyByBase(baseInk) : familyFor(teamId);
  return assemble(family, liverySeedHash(`${teamId}/roster`) + memberIndex * 7, teamId);
}

/**
 * What the boards actually call. An independent is painted off his own id; a
 * team car off his team's, varied by roster slot when the roster is known and
 * by his driver id when it is not. `baseInk` is the shop's dealt colour, when
 * the caller has the garage to hand.
 */
export function liveryForDriver(
  driverId: string,
  teamId?: string,
  memberIndex?: number,
  baseInk?: InkName,
  make?: Manufacturer,
): Livery {
  const painted = !teamId
    ? liveryFor(driverId, undefined, baseInk)
    : memberIndex !== undefined && memberIndex >= 0
      ? liveryForTeamMember(teamId, memberIndex, baseInk)
      : liveryFor(teamId, driverId, baseInk);
  /*
    ⚠ THE MAKE ON THE NOSE TAB IS A FACT WHEN THE CARD PRINTS ONE — D-EZ-76.

    A generated driver has no make, so `manufacturerFor` hashes him one and that
    is the right answer for an invented field. A PLAAY card prints the make on
    the nose tab, and the 2025 set prints it for all fifty-one — so hashing it
    put Brad Keselowski in a PONTIAC on the 2025 grid, a marque that has not
    raced since 2003. Caught by looking at the screen, which is the only place
    it was ever going to show.

    Overridden here rather than inside `assemble`, so the paint is untouched:
    the shop keeps its base, accent, scheme and decal, and only the tab changes.
  */
  return make ? { ...painted, manufacturer: make } : painted;
}
